import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiEndpoints } from '../services/api';
import { handleApiError } from '../services/api';
import { toast } from 'react-hot-toast';
import { 
  Plus, Edit, Trash2, RefreshCw, Search, Package, History,
  ChevronDown, ChevronRight, X, Minus, ArrowUpDown
} from 'lucide-react';
import { debounce } from 'lodash';

const SimpleInventoryManagement = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [activeTab, setActiveTab] = useState('inventory');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventorySummary, setInventorySummary] = useState(null);
  const [inventoryHistory, setInventoryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [viewingItemHistory, setViewingItemHistory] = useState(null);
  const [itemHistory, setItemHistory] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [itemHistories, setItemHistories] = useState({});
  const [historyPagination, setHistoryPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  
  const [suppliers, setSuppliers] = useState([]);
  const [itemData, setItemData] = useState({
    name: '',
    category: 'Equipment',
    brand: '',
    model: '',
    unit: 'pieces',
    currentQuantity: 0,
    minimumQuantity: 0,
    supplierId: null,
    location: '',
    notes: ''
  });

  const [adjustData, setAdjustData] = useState({
    quantity: 0,
    reason: '',
    notes: '',
    operation: 'add'
  });

  // Pagination and Filters for Inventory Items
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Pagination and Filters for History
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyItemsPerPage, setHistoryItemsPerPage] = useState(10);
  const [historyTotalItems, setHistoryTotalItems] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historySearchTerm, setHistorySearchTerm] = useState('');

    const [refreshKey, setRefreshKey] = useState(0);
const inventoryCategories = [
    'Equipment', 'Feed', 'Supplies', 'Medical', 'Maintenance', 'Safety', 'Other'
  ];
  const inventoryUnits = ['pieces', 'kg', 'liters', 'boxes', 'bags', 'meters', 'sets'];
  const transactionTypes = [
    'Purchase', 'Sale', 'Adjustment', 'Usage', 'Return'
  ];

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await apiEndpoints.suppliers.getAll();
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, []);

  const fetchInventoryData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        summaryResponse,
        itemsResponse,
        historyResponse
      ] = await Promise.all([
        apiEndpoints.inventory.getSummary(),
        apiEndpoints.inventory.getPaginated({
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          category: categoryFilter
        }),
        apiEndpoints.inventory.getAllHistory({
          page: historyCurrentPage,
          limit: historyItemsPerPage,
          search: historySearchTerm
        })
      ]);

      setInventorySummary(summaryResponse.data);
      setInventoryItems(itemsResponse.data.items);
      setTotalItems(itemsResponse.data.totalItems);
      setTotalPages(itemsResponse.data.totalPages);

      setInventoryHistory(historyResponse.data.history);
      setHistoryTotalItems(historyResponse.data.totalItems);
      setHistoryTotalPages(historyResponse.data.totalPages);

    } catch (error) {
      handleApiError(error, t('inventory.messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [
    currentPage, itemsPerPage, searchTerm, categoryFilter,
    historyCurrentPage, historyItemsPerPage, historySearchTerm, t
  ]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    fetchInventoryData();
  }, [fetchInventoryData, currentPage, itemsPerPage, historyCurrentPage, historyItemsPerPage, refreshKey]);

  const debouncedSearch = useCallback(
    debounce(() => {
      setCurrentPage(1);
      fetchInventoryData();
    }, 500),
    [fetchInventoryData]
  );

  useEffect(() => {
    if (searchTerm) {
      debouncedSearch();
    } else {
      fetchInventoryData();
    }
    return debouncedSearch.cancel;
  }, [searchTerm, debouncedSearch]);

  const handleFilterChange = (filterName, value) => {
    if (filterName === 'category') setCategoryFilter(value);
    setCurrentPage(1);
    fetchInventoryData();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setCurrentPage(1);
    fetchInventoryData();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleHistoryPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= historyTotalPages) {
      setHistoryCurrentPage(newPage);
    }
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setItemData({
      name: '',
      category: 'Equipment',
      brand: '',
      model: '',
      unit: 'pieces',
      currentQuantity: 0,
      minimumQuantity: 0,
      supplierId: null,
      location: '',
      notes: ''
    });
    setShowItemModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemData({
      name: item.name || '',
      category: item.category || 'Equipment',
      brand: item.brand || '',
      model: item.model || '',
      unit: item.unit || 'pieces',
      currentQuantity: item.currentQuantity || 0,
      minimumQuantity: item.minimumQuantity || 0,
      supplierId: item.supplierId || null,
      location: item.location || '',
      notes: item.notes || ''
    });
    setShowItemModal(true);
  };

  const handleAdjustQuantity = (item) => {
    setAdjustingItem(item);
    setAdjustData({
      quantity: 0,
      reason: '',
      notes: ''
    });
    setShowAdjustModal(true);
  };

  const handleQuickAdjust = (item, operation) => {
    setAdjustingItem(item);
    setAdjustData({
      quantity: 0,
      reason: '',
      notes: '',
      operation: operation
    });
    setShowAdjustModal(true);
  };

  const handleViewItemHistory = async (item, page = 1) => {
    setViewingItemHistory(item);
    setLoading(true);
    try {
      const response = await apiEndpoints.inventory.getItemHistory(item.id, { 
        page: page, 
        limit: historyPagination.itemsPerPage 
      });
      setItemHistory(response.data.history || []);
      setHistoryPagination({
        currentPage: response.data.currentPage || page,
        totalPages: response.data.totalPages || 1,
        totalItems: response.data.totalItems || 0,
        itemsPerPage: historyPagination.itemsPerPage
      });
      setShowHistoryModal(true);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleItemExpansion = async (itemId) => {
    const newExpandedItems = new Set(expandedItems);
    
    if (expandedItems.has(itemId)) {
      // Collapse
      newExpandedItems.delete(itemId);
      setExpandedItems(newExpandedItems);
    } else {
      // Expand - fetch history if not already loaded
      newExpandedItems.add(itemId);
      setExpandedItems(newExpandedItems);
      
      if (!itemHistories[itemId]) {
        try {
          const response = await apiEndpoints.inventory.getItemHistory(itemId, { page: 1, limit: 20 });
          setItemHistories(prev => ({
            ...prev,
            [itemId]: response.data.history || []
          }));
        } catch (error) {
          handleApiError(error);
        }
      }
    }
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm(t('inventory.messages.confirmDelete'))) {
      try {
        await apiEndpoints.inventory.delete(id);
        toast.success(t('inventory.messages.itemDeleted'));
        fetchInventoryData();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
        handleApiError(error, t('inventory.messages.failedToDelete'));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiData = {
        ...itemData,
        currentQuantity: parseFloat(itemData.currentQuantity),
        minimumQuantity: parseFloat(itemData.minimumQuantity),
        unitCost: 0 // Set to 0 since we're only tracking quantities
      };

      if (editingItem) {
        await apiEndpoints.inventory.update(editingItem.id, apiData);
        toast.success(t('inventory.messages.itemUpdated'));
      } else {
        await apiEndpoints.inventory.create(apiData);
        toast.success(t('inventory.messages.itemCreated'));
      }
      setShowItemModal(false);
      setEditingItem(null);
      fetchInventoryData();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      handleApiError(error, t('inventory.messages.failedToSave'));
    }
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    try {
      const quantity = parseFloat(adjustData.quantity);
      const adjustedQuantity = adjustData.operation === 'add' ? quantity : -quantity;
      
      await apiEndpoints.inventory.adjustQuantity(adjustingItem.id, {
        Quantity: adjustedQuantity,
        Reason: adjustData.operation === 'add' ? 'Stock Addition' : 'Stock Reduction',
        Notes: adjustData.notes
      });
      toast.success(t('inventory.messages.quantityAdjusted'));
      setShowAdjustModal(false);
      setAdjustingItem(null);
      setAdjustData({ quantity: 0, reason: '', notes: '', operation: 'add' });
      fetchInventoryData();
      setRefreshKey(prev => prev + 1); // Force component refresh
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      handleApiError(error, t('inventory.messages.failedToAdjust'));
    }
  };

  const getTransactionTypeText = (type) => {
    switch (type) {
      case 0: return t('inventory.transactionTypes.purchase');
      case 1: return t('inventory.transactionTypes.sale');
      case 2: return t('inventory.transactionTypes.adjustment');
      case 3: return t('inventory.transactionTypes.usage');
      case 4: return t('inventory.transactionTypes.return');
      default: return t('inventory.transactionTypes.purchase');
    }
  };

  const getCategoryTranslation = (category) => {
    return t(`inventory.categories.${category.toLowerCase()}`);
  };

  const getUnitTranslation = (unit) => {
    return t(`inventory.units.${unit.toLowerCase()}`);
  };

  const renderPagination = (currentPage, totalPages, handlePageChange) => (
    <div className="flex justify-between items-center mt-4">
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50"
      >
        {t('inventory.previous')}
      </button>
      <span>
        {t('inventory.showingResults', {
          start: (currentPage - 1) * itemsPerPage + 1,
          end: Math.min(currentPage * itemsPerPage, totalItems),
          total: totalItems
        })}
      </span>
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50"
      >
        {t('inventory.next')}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen" key={refreshKey}>
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
        <p className="ml-4 text-gray-700">{t('inventory.loadingInventory')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" key={refreshKey}>
        {/* Fixed Header Section */}
        <div className="flex-shrink-0">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('inventory.title')}</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">{t('inventory.subtitle')}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-4">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('inventory')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
                    activeTab === 'inventory'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Package className="w-4 h-4 mr-2" />
                  {t('inventory.inventory')}
                </button>
              </nav>
            </div>
          </div>

          {/* Filters and Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="relative flex-grow">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('inventory.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <select
                  value={categoryFilter}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('inventory.allCategories')}</option>
                  {inventoryCategories.map(cat => (
                    <option key={cat} value={cat}>{getCategoryTranslation(cat)}</option>
                  ))}
                </select>

                <div className="flex gap-2">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t('inventory.clear')}
                  </button>
                  <button
                    onClick={fetchInventoryData}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('inventory.refresh')}
                  </button>
                  <button
                    onClick={handleAddItem}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('inventory.addItem')}
                  </button>
                </div>
              </div>
            </div>
        </div>

        {/* Scrollable Content Section */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
          {activeTab === 'inventory' && (
            <div>
              {/* Inventory Items Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('inventory.inventoryItems')}</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.itemName')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.category')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.quantity')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.minimumQuantity')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {inventoryItems.map(item => (
                      <React.Fragment key={item.id}>
                        <tr className={`hover:bg-gray-50 ${item.currentQuantity <= item.minimumQuantity ? 'bg-red-50 border-l-4 border-red-400' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div className="flex items-center">
                              <button
                                onClick={() => toggleItemExpansion(item.id)}
                                className="mr-2 p-1 hover:bg-gray-200 rounded"
                              >
                                {expandedItems.has(item.id) ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </button>
                              {item.name}
                              {item.currentQuantity <= item.minimumQuantity && (
                                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  ⚠️ {t('inventory.lowStock')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getCategoryTranslation(item.category)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`${item.currentQuantity <= item.minimumQuantity ? 'text-red-600 font-bold bg-red-100 px-2 py-1 rounded' : 'text-gray-500'}`}>
                              {item.currentQuantity} {getUnitTranslation(item.unit)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`${item.currentQuantity <= item.minimumQuantity ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                              {item.minimumQuantity} {getUnitTranslation(item.unit)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleQuickAdjust(item, 'add')}
                                className="text-green-600 hover:text-green-900"
                                title={t('inventory.addQuantity')}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleQuickAdjust(item, 'subtract')}
                                className="text-red-600 hover:text-red-900"
                                title={t('inventory.subtractQuantity')}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleViewItemHistory(item)}
                                className="text-purple-600 hover:text-purple-900"
                                title={t('inventory.viewHistory')}
                              >
                                <History className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditItem(item)}
                                className="text-blue-600 hover:text-blue-900"
                                title={t('common.edit')}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="text-red-600 hover:text-red-900"
                                title={t('common.delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded History Row */}
                        {expandedItems.has(item.id) && (
                          <tr>
                            <td colSpan="4" className="px-6 py-4 bg-gray-50">
                              <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                  <History className="w-4 h-4 mr-2" />
                                  {t('inventory.transactionHistory')} - {item.name}
                                </h4>
                                
                                {itemHistories[item.id] ? (
                                  itemHistories[item.id].length > 0 ? (
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-xs">
                                        <thead className="bg-gray-100">
                                          <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-600">{t('inventory.quantity')}</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-600">{t('inventory.description')}</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-600">{t('inventory.transactionDate')}</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                          {itemHistories[item.id].map((transaction, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                              <td className="px-3 py-2 text-gray-700">
                                                {(() => {
                                                  const isReduction = transaction.description?.includes('Stock Reduction') || 
                                                                      transaction.description?.includes('Reduction');
                                                  const isAddition = transaction.description?.includes('Stock Addition') || 
                                                                     transaction.description?.includes('Addition') ||
                                                                     transaction.description?.includes('Initial stock');
                                                  const displayQuantity = isReduction ? -Math.abs(transaction.quantity) : Math.abs(transaction.quantity);
                                                  const isPositive = displayQuantity > 0;
                                                  
                                                  return (
                                                    <span className={`font-medium ${
                                                      isPositive ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                      {isPositive ? '+' : ''}{displayQuantity}
                                                    </span>
                                                  );
                                                })()}
                                              </td>
                                              <td className="px-3 py-2 text-gray-700">{transaction.description || '-'}</td>
                                              <td className="px-3 py-2 text-gray-700">{new Date(transaction.transactionDate).toLocaleDateString()}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <p className="text-gray-500 text-sm">{t('inventory.noHistory')}</p>
                                  )
                                ) : (
                                  <div className="flex items-center justify-center py-4">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                    <span className="ml-2 text-sm text-gray-500">{t('inventory.loadingHistory')}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            </div>
          )}
        </div>

        {/* Pagination - Sticky at bottom */}
        {activeTab === 'inventory' && (
          <div className="sticky bottom-0 bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 z-10 flex-shrink-0">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
              <div className="text-xs sm:text-sm text-gray-700 font-medium">
                {t('inventory.showingResults', {
                  start: ((currentPage - 1) * itemsPerPage) + 1,
                  end: Math.min(currentPage * itemsPerPage, totalItems),
                  total: totalItems
                })}
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
                >
                  {t('inventory.previous')}
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const startPage = Math.max(1, currentPage - 2);
                    const pageNum = startPage + i;
                    if (pageNum > totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] touch-manipulation ${
                          pageNum === currentPage
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
                >
                  {t('inventory.next')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Item Modal */}
        {showItemModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingItem ? t('inventory.editItem') : t('inventory.addNewItem')}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">{t('inventory.itemName')} *</label>
                    <input
                      type="text"
                      id="name"
                      value={itemData.name}
                      onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">{t('inventory.category')} *</label>
                    <select
                      id="category"
                      value={itemData.category}
                      onChange={(e) => setItemData({ ...itemData, category: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      required
                    >
                      {inventoryCategories.map(cat => (
                        <option key={cat} value={cat}>{getCategoryTranslation(cat)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="brand" className="block text-sm font-medium text-gray-700">{t('inventory.brand')}</label>
                    <input
                      type="text"
                      id="brand"
                      value={itemData.brand}
                      onChange={(e) => setItemData({ ...itemData, brand: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    />
                  </div>
                  <div>
                    <label htmlFor="model" className="block text-sm font-medium text-gray-700">{t('inventory.model')}</label>
                    <input
                      type="text"
                      id="model"
                      value={itemData.model}
                      onChange={(e) => setItemData({ ...itemData, model: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="unit" className="block text-sm font-medium text-gray-700">{t('inventory.unit')} *</label>
                    <select
                      id="unit"
                      value={itemData.unit}
                      onChange={(e) => setItemData({ ...itemData, unit: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      required
                    >
                      {inventoryUnits.map(unit => (
                        <option key={unit} value={unit}>{getUnitTranslation(unit)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="currentQuantity" className="block text-sm font-medium text-gray-700">{t('inventory.currentQuantity')} *</label>
                    <input
                      type="number"
                      id="currentQuantity"
                      value={itemData.currentQuantity}
                      onChange={(e) => setItemData({ ...itemData, currentQuantity: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="minimumQuantity" className="block text-sm font-medium text-gray-700">{t('inventory.minimumQuantity')}</label>
                  <input
                    type="number"
                    id="minimumQuantity"
                    value={itemData.minimumQuantity}
                    onChange={(e) => setItemData({ ...itemData, minimumQuantity: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    min="0"
                    step="0.01"
                    placeholder={t('inventory.minimumQuantityPlaceholder')}
                  />
                  <p className="mt-1 text-sm text-gray-500">{t('inventory.minimumQuantityHelp')}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="supplier" className="block text-sm font-medium text-gray-700">{t('inventory.supplier')}</label>
                    <select
                      id="supplier"
                      value={itemData.supplierId || ''}
                      onChange={(e) => setItemData({ ...itemData, supplierId: e.target.value ? parseInt(e.target.value) : null })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    >
                      <option value="">{t('inventory.selectSupplier') || 'Sélectionner un fournisseur'}</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700">{t('inventory.location')}</label>
                    <input
                      type="text"
                      id="location"
                      value={itemData.location}
                      onChange={(e) => setItemData({ ...itemData, location: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                    />
                  </div>
                </div>


                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">{t('inventory.notes')}</label>
                  <textarea
                    id="notes"
                    value={itemData.notes}
                    onChange={(e) => setItemData({ ...itemData, notes: e.target.value })}
                    rows="2"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  ></textarea>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowItemModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                  >
                    {t('inventory.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingItem ? t('inventory.updateItem') : t('inventory.addItem')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Adjust Quantity Modal */}
        {showAdjustModal && adjustingItem && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {adjustData.operation === 'add' ? t('inventory.addQuantity') : t('inventory.subtractQuantity')} - {adjustingItem.name}
              </h2>
              
              <form onSubmit={handleAdjustSubmit} className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('inventory.currentQuantity')}: <span className="font-semibold">{adjustingItem.currentQuantity} {getUnitTranslation(adjustingItem.unit)}</span>
                  </p>
                </div>

                <div>
                  <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                    {adjustData.operation === 'add' 
                      ? `${t('inventory.quantity')} ${t('inventory.toAdd')} *` 
                      : `${t('inventory.quantity')} ${t('inventory.toSubtract')} *`
                    }
                  </label>
                  <input
                    type="number"
                    id="quantity"
                    value={adjustData.quantity}
                    onChange={(e) => setAdjustData({ ...adjustData, quantity: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    min="0"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {adjustData.operation === 'add' 
                      ? t('inventory.addDescription')
                      : t('inventory.subtractDescription')
                    }
                  </p>
                </div>


                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                    {t('inventory.notes')}
                  </label>
                  <textarea
                    id="notes"
                    value={adjustData.notes}
                    onChange={(e) => setAdjustData({ ...adjustData, notes: e.target.value })}
                    rows="2"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  ></textarea>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAdjustModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                  >
                    {t('inventory.cancel')}
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 text-white rounded-md flex items-center ${
                      adjustData.operation === 'add' 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {adjustData.operation === 'add' ? (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        {t('inventory.addQuantity')}
                      </>
                    ) : (
                      <>
                        <Minus className="w-4 h-4 mr-2" />
                        {t('inventory.subtractQuantity')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Item History Modal */}
        {showHistoryModal && viewingItemHistory && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {t('inventory.transactionHistory')} - {viewingItemHistory.name}
                  </h2>
                  <button
                    onClick={() => setShowHistoryModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">{t('common.loading')}</span>
                  </div>
                ) : itemHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {t('inventory.noHistoryFound')}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('inventory.quantity')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('inventory.description')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('inventory.transactionDate')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {itemHistory.map(transaction => (
                          <tr key={transaction.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(() => {
                                const isReduction = transaction.description?.includes('Stock Reduction') || 
                                                   transaction.description?.includes('Reduction');
                                const isAddition = transaction.description?.includes('Stock Addition') || 
                                                  transaction.description?.includes('Addition') ||
                                                  transaction.description?.includes('Initial stock');
                                const displayQuantity = isReduction ? -Math.abs(transaction.quantity) : Math.abs(transaction.quantity);
                                const isPositive = displayQuantity > 0;
                                
                                return (
                                  <span className={`font-medium ${
                                    isPositive ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {isPositive ? '+' : ''}{displayQuantity}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {transaction.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(transaction.transactionDate).toLocaleDateString()} {new Date(transaction.transactionDate).toLocaleTimeString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-700">
                  {t('common.showing')} {((historyPagination.currentPage - 1) * historyPagination.itemsPerPage) + 1} {t('common.to')} {Math.min(historyPagination.currentPage * historyPagination.itemsPerPage, historyPagination.totalItems)} {t('common.of')} {historyPagination.totalItems} {t('common.results')}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleViewItemHistory(viewingItemHistory, historyPagination.currentPage - 1)}
                    disabled={historyPagination.currentPage <= 1}
                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.previous')}
                  </button>
                  <span className="text-sm text-gray-700">
                    {t('common.page')} {historyPagination.currentPage} {t('common.of')} {historyPagination.totalPages}
                  </span>
                  <button
                    onClick={() => handleViewItemHistory(viewingItemHistory, historyPagination.currentPage + 1)}
                    disabled={historyPagination.currentPage >= historyPagination.totalPages}
                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.next')}
                  </button>
                  <button
                    onClick={() => setShowHistoryModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 ml-4"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default SimpleInventoryManagement;
